import { useEffect, useRef, useCallback } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useReceiptType } from '../context/SettingsContext';
import logger from '../utils/logger';

/**
 * Hook to automatically send read receipts for messages in view
 * Implements TI requirements for read receipt handling
 * 
 * UPDATED: Now includes Netsfere-inspired patterns:
 * - Uses getLastVisibleEventId for deterministic last visible message
 * - Lifecycle-driven marking (mount, scroll, focus)
 * - Better debouncing logic
 * 
 * @param {string} roomId - The room ID
 * @param {Array} visibleEventIds - Array of event IDs currently visible to the user
 * @param {boolean} isActive - Whether the chat view is currently active/focused
 * @param {Function} getLastVisibleEventId - Function to get the last visible event ID (from useVisibleMessages)
 * @returns {Object} - sendReceipt function for manual receipt sending
 */
export const useReadReceipts = (roomId, visibleEventIds = [], isActive = true, getLastVisibleEventId = null) => {
  const client = useMatrixClient();
  const receiptType = useReceiptType();
  const lastReceiptSent = useRef(new Map()); // Track last receipt per room
  const pendingReceipts = useRef(new Map()); // Debounce receipts

  // Clear sent receipts cache when receipt type changes
  // This ensures receipts are re-sent with the new type
  useEffect(() => {
    logger.debug('Receipt type changed, clearing sent receipts cache', { receiptType });
    lastReceiptSent.current.clear();
  }, [receiptType]);

  /**
   * Send a read receipt for a specific event
   * @param {string} eventId - Event ID to mark as read
   * @returns {Promise<void>}
   */
  const sendReceipt = useCallback(async (eventId) => {
    if (!client || !roomId || !eventId || !receiptType) {
      logger.debug('Read receipt skipped - missing requirements:', { 
        hasClient: !!client, 
        roomId, 
        eventId, 
        receiptType, 
      });
      return;
    }

    // Check if we already sent a receipt for this event in this room
    const lastSent = lastReceiptSent.current.get(roomId);
    if (lastSent === eventId) {
      logger.debug('Read receipt already sent for event:', eventId);
      return;
    }

    try {
      const room = client.getRoom(roomId);
      if (!room) {
        logger.warn('Room not found for read receipt:', roomId);
        return;
      }

      const event = room.findEventById(eventId);
      if (!event) {
        logger.warn('Event not found for read receipt:', eventId);
        return;
      }

      // Don't send read receipts for our own messages
      const currentUserId = client.getUserId();
      const messageSender = event.getSender();
      if (messageSender === currentUserId) {
        logger.debug('Skipping read receipt for own message:', eventId);
        return;
      }

      // Send read receipt
      //
      // IMPORTANT NOTE ABOUT UNREAD BADGES:
      // Both receipt types update your local unread count:
      // - Public receipts (m.read): Visible to all room members
      // - Private receipts (m.read.private): Only visible to the message sender
      //
      // The Matrix SDK updates the local unread count for BOTH types and fires
      // RoomEvent.UnreadNotifications when the count changes.

      logger.info('Sending read receipt:', {
        roomId,
        eventId,
        receiptType,
        sender: messageSender,
        isPrivate: receiptType === 'm.read.private',
      });

      // Send the read receipt
      const receiptResult = await client.sendReadReceipt(event, receiptType, true);

      lastReceiptSent.current.set(roomId, eventId);
      logger.info('✅ Read receipt sent successfully:', {
        roomId,
        eventId,
        receiptType,
        result: receiptResult,
        message: receiptType === 'm.read' ? 'Badge will update' : 'Badge may not update (private receipt)',
      });
    } catch (error) {
      logger.error('❌ Failed to send read receipt:', error);
    }
  }, [client, roomId, receiptType]);

  /**
   * Send receipt for the most recent visible event
   * Debounced to avoid sending too many receipts
   * 
   * Uses getLastVisibleEventId if available for more accurate detection
   */
  const sendReceiptForLatestVisible = useCallback(() => {
    if (!isActive) {
      logger.debug('Read receipt skipped - chat not active');
      return;
    }

    // Try to get last visible from function if available (more accurate)
    let latestEventId = null;
    if (getLastVisibleEventId && typeof getLastVisibleEventId === 'function') {
      latestEventId = getLastVisibleEventId();
    }

    // Fallback to array if function didn't work
    if (!latestEventId && visibleEventIds && visibleEventIds.length > 0) {
      latestEventId = visibleEventIds[visibleEventIds.length - 1];
    }

    if (!latestEventId) {
      logger.debug('Read receipt skipped - no latest event ID');
      return;
    }

    logger.debug('Scheduling read receipt for latest event:', {
      roomId,
      latestEventId,
      totalVisible: visibleEventIds.length,
      source: getLastVisibleEventId ? 'function' : 'array',
    });

    // Clear any pending receipt for this room
    const pending = pendingReceipts.current.get(roomId);
    if (pending) {
      clearTimeout(pending);
      logger.debug('Cleared previous pending receipt');
    }

    // Debounce receipt sending by 1 second
    const timeoutId = setTimeout(() => {
      logger.debug('Debounce timeout complete, sending receipt now');
      sendReceipt(latestEventId);
      pendingReceipts.current.delete(roomId);
    }, 1000);

    pendingReceipts.current.set(roomId, timeoutId);
  }, [roomId, visibleEventIds, isActive, sendReceipt, getLastVisibleEventId]);

  // Send receipt when visible events change
  useEffect(() => {
    sendReceiptForLatestVisible();

    // Cleanup pending receipts on unmount
    return () => {
      const pending = pendingReceipts.current.get(roomId);
      if (pending) {
        clearTimeout(pending);
        pendingReceipts.current.delete(roomId);
        logger.debug('Cleaned up pending receipt on unmount');
      }
    };
  }, [roomId, visibleEventIds, sendReceiptForLatestVisible]);

  // Lifecycle-driven: Mark read on window focus (Netsfere approach)
  // This ensures receipts are sent when user returns to the tab
  useEffect(() => {
    const handleFocus = () => {
      logger.debug('Window focused, triggering read receipt check');
      sendReceiptForLatestVisible();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [sendReceiptForLatestVisible]);

  return {
    sendReceipt, // Expose for manual receipt sending if needed
    markRead: sendReceiptForLatestVisible, // Expose for manual triggering
  };
};

export default useReadReceipts;
